import { COLUMN, COMMITTEE, FORMATTING, JARGON, KIND, LAYOUT, POSITION } from "../../modules/constants"
import { abbreviateMembers } from "../../modules/legends"
import { addPaddingRightToLastCell, calcStats, firstToLast, getCell, getCellWithTooltip, headCell, setStats } from "../../modules/table.funcs"
import { initStats2 } from "../../components/layout/InitStats"
import { commonLseTableData } from "./CreateLseCommonTableData"

const { border1, float, integer, left, normal, number, percent, right, string, yearsMonths } = FORMATTING
const stats = {}
const ageWidth = "5%"
const audCom = 0
const remCom = 1
const nomCom = 2

export const CreateLseRolesCommitteesHeaderData = props => {
    // console.log('CreateLseRolesCommitteesHeaderData props:', props)
    const { portrait, showLogos } = props
    const committeeWidth = portrait ? null : LAYOUT.COMMITTEE.WIDTH
    const committeeAlign = LAYOUT.COMMITTEE.ALIGNMENT
    const borderLeft = portrait ? null : border1
    const columns = props.columns[KIND.LSE.ROLES.COMMITTEES]

    const company = headCell({
        paddingLeft: showLogos ? 0 : LAYOUT.HOME.PADDING.BORDER,
        align: left,
        id: COLUMN.COMPANY,
    })

    const boardRole = headCell({
        align: left,
        borderLeft,
        id: COLUMN.ROLE,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
    })

    const tenure = headCell({
        id: COLUMN.TENURE,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
    })

    const sex = headCell({
        id: COLUMN.SEX,
        width: ageWidth,
    })

    const officer = headCell({
        align: left,
        id: COLUMN.OFFICER,
    })

    const ac = headCell({
        align: committeeAlign,
        borderLeft,
        id: COLUMN.COMMITTEE.AUDIT.CHAIRS,
        width: committeeWidth,
    })

    const am = headCell({
        align: committeeAlign,
        id: COLUMN.COMMITTEE.AUDIT.MEMBER,
        width: committeeWidth,
        label: abbreviateMembers({ committee: COLUMN.COMMITTEE.AUDIT, portrait })
    })

    const rc = headCell({
        align: committeeAlign,
        borderLeft,
        id: COLUMN.COMMITTEE.REMUNERATION.CHAIRS,
        width: committeeWidth,
    })

    const rm = headCell({
        align: committeeAlign,
        id: COLUMN.COMMITTEE.REMUNERATION.MEMBER,
        width: committeeWidth,
    })

    const nc = headCell({
        align: committeeAlign,
        borderLeft,
        id: COLUMN.COMMITTEE.NOMINATION.CHAIRS,
        width: committeeWidth,
    })

    const nm = headCell({
        align: committeeAlign,
        id: COLUMN.COMMITTEE.NOMINATION.MEMBER,
        width: committeeWidth,
    })

    const age = headCell({
        borderLeft,
        id: COLUMN.AGE,
        width: ageWidth,
    })

    const headerCells = portrait
        ? [company, officer, ac, am]
        : [company, officer, boardRole]

    if (!portrait) {
        if (columns[COLUMN.TENURE]) headerCells.push(tenure)
        headerCells.push(ac, am, rc, rm, nc, nm)
        if (columns[COLUMN.AGE]) headerCells.push(age)
        if (columns[COLUMN.SEX]) headerCells.push(sex)
    }

    if (showLogos) {
        const logoHead = headCell({
            align: left,
            id: COLUMN.LOGO,
            width: LAYOUT.LOGO_WIDTH,
        })

        headerCells.splice(0, 0, logoHead) // insert logo header
    }

    if (!portrait)
        addPaddingRightToLastCell(headerCells)

    return headerCells
}

const getCommitteeFooterCells = (column, borderLeft) => ({
    total: getCell({
        align: LAYOUT.COMMITTEE.ALIGNMENT,
        borderLeft,
        column,
        type: integer,
        value: stats[column].total,
    }),
    mean: getCell({
        borderLeft,
        column,
    }),
})

export const createLseRolesCommitteesFootersData = props => {
    // console.log('createLseRolesCommitteesFootersData props:', props)
    const { portrait, showLogos } = props
    const borderLeft = portrait ? null : border1
    const columns = props.columns[KIND.LSE.ROLES.COMMITTEES]
    let column = null

    for (const property in stats)
        calcStats(stats[property])

    column = COLUMN.COMPANY
    const company = {
        total: getCell({
            align: left,
            column,
            paddingLeft: showLogos ? 0 : LAYOUT.HOME.PADDING.BORDER,
            suffix: ` ${stats[column].total === 1 ? JARGON.APPT : JARGON.APPTS}`,
            type: integer,
            value: stats[column].total,
        }),
        mean: getCell({
            align: left,
            column,
            paddingLeft: showLogos ? 0 : LAYOUT.HOME.PADDING.BORDER,
        }),
    }

    column = COLUMN.LEGEND
    const legend = {
        total: getCell({
            column,
            label: "Total:",
        }),
        mean: getCell({
            column,
            label: "Average:",
        }),
    }

    column = COLUMN.TENURE
    const tenure = {
        total: getCell({
            column,
            type: yearsMonths,
            value: stats[column].total,
        }),
        mean: getCell({
            column,
            type: yearsMonths,
            value: stats[column].mean,
        }),
    }

    const ac = getCommitteeFooterCells(COLUMN.COMMITTEE.AUDIT.CHAIRS, borderLeft)
    const am = getCommitteeFooterCells(COLUMN.COMMITTEE.AUDIT.MEMBER)
    const rc = getCommitteeFooterCells(COLUMN.COMMITTEE.REMUNERATION.CHAIRS, borderLeft)
    const rm = getCommitteeFooterCells(COLUMN.COMMITTEE.REMUNERATION.MEMBER)
    const nc = getCommitteeFooterCells(COLUMN.COMMITTEE.NOMINATION.CHAIRS, borderLeft)
    const nm = getCommitteeFooterCells(COLUMN.COMMITTEE.NOMINATION.MEMBER)

    column = COLUMN.OFFICER
    const officer = {
        total: getCell({ column }),
        mean: getCell({ column }),
    }

    column = COLUMN.AGE
    const age = {
        total: getCell({
            borderLeft,
            column,
            type: integer,
            value: stats[column].total
        }),
        mean: getCell({
            borderLeft,
            column,
            type: float,
            value: stats[column].mean,
        }),
    }

    column = COLUMN.SEX
    const sex = {
        total: getCell({
            column,
            suffix: " F",
            type: integer,
            value: stats[column].total,

        }),
        mean: getCell({
            column,
            suffix: "% F",
            type: percent,
            value: stats[column].mean,
        }),
    }

    const footCells = {
        totals: portrait
            ? [company.total, officer.total, ac.total, am.total]
            : [company.total, officer.total, legend.total],

        means: portrait
            ? [company.mean, officer.mean, ac.mean, am.mean]
            : [company.mean, officer.mean, legend.mean],
    }

    if (!portrait) {
        if (columns[COLUMN.TENURE]) {
            footCells.totals.push(tenure.total)
            footCells.means.push(tenure.mean)
        }
        footCells.totals.push(ac.total, am.total, rc.total, rm.total, nc.total, nm.total)
        footCells.means.push(ac.mean, am.mean, rc.mean, rm.mean, nc.mean, nm.mean)
        if (columns[COLUMN.AGE]) {
            footCells.totals.push(age.total)
            footCells.means.push(age.mean)
        }
        if (columns[COLUMN.SEX]) {
            footCells.totals.push(sex.total)
            footCells.means.push(sex.mean)
        }
    }

    if (showLogos) {
        const logoFoot = headCell({ id: COLUMN.LOGO, align: left })

        footCells.totals.splice(0, 0, logoFoot) // insert logo footer
        footCells.means.splice(0, 0, logoFoot) // insert logo footer
    }

    if (!portrait) {
        addPaddingRightToLastCell(footCells.totals)
        addPaddingRightToLastCell(footCells.means)
    }

    return footCells
}

//#region Rows
export const createLseRolesCommitteesRowData = props => {
    // console.log("createLseRolesCommitteesRowData props: ", props)
    const { items, lseCompaniesMeta, portrait, showLogos } = props
    const columns = props.columns[KIND.LSE.ROLES.COMMITTEES]
    const borderLeft = portrait ? null : border1
    const rows = []
    initStats2(stats)
    const tooltip = ""

    items.forEach(item => {
        rows.push(createLseRolesCommitteesRow({ borderLeft, columns, item, lseCompaniesMeta, portrait, showLogos, tooltip }))
    })

    return rows
}

const committees = [COMMITTEE.AUDIT, COMMITTEE.REMCOM, COMMITTEE.NOMCOM]
const noCommittee = (committee, lseCompanyMeta) => ({
    backgroundColor: COMMITTEE.NONEXISTANT.COLOR,
    align: LAYOUT.COMMITTEE.ALIGNMENT,
    sortValue: 2,
    tooltip: `${lseCompanyMeta.names[0]} has no ${committees[committee]} Committee`,
})

// Returns obj with value: "n/a" + tooltip if no committee - indicated by -1. Else value: ""
// committee is either 1 - remCom, or 2 - nomCom. The is always an audit committee (0)
const testForNoCommittee = (bool, committee, lseCompanyMeta) => {
    return bool
        ? {
            align: LAYOUT.COMMITTEE.ALIGNMENT,
            sortValue: -1, // Use low number for sorting so that this is at the top when first sorted
            value: LAYOUT.TICK,
        }
        : lseCompanyMeta.cmtes[committee][0] === -1
            ? noCommittee(committee, lseCompanyMeta)
            : {
                sortValue: 0,
                value: "",
            }
}

// Sets the stats for the column provided and returns the 'CellWithTooltip'
const setStatsAndGetCell = (column, cellSpec, stats) => {
    setStats(cellSpec[column].value === LAYOUT.TICK, stats[column], true)
    return getCellWithTooltip(cellSpec[column])
}

const createLseRolesCommitteesRow = props => {
    // console.log('createLseRolesRow props:', props)
    const { borderLeft, columns, item, lseCompaniesMeta, portrait, showLogos, tooltip } = props

    const { apptInfo, committees, companyInfo, commonInfo, index, personInfo } = item
    // const position = apptInfo.position.title ? apptInfo.position.title : apptInfo.role
    const fontWeight = (apptInfo && apptInfo.position)
        ? getPositionFontWeight(apptInfo.position.typeBit)
        : normal

    // Aquire the lseCompanyMeta from lseCompaniesMeta via the companyMetaIndex
    const lseCompanyMeta = lseCompaniesMeta.items[companyInfo.companyMetaIndex]
    // console.log('lseCompanyMeta:', lseCompanyMeta)
    const cellSpec = {
        [COLUMN.COMMITTEE.AUDIT.CHAIRS]: {
            borderLeft,
            ...testForNoCommittee(committees?.a === 1, audCom, lseCompanyMeta),
        },
        [COLUMN.COMMITTEE.AUDIT.MEMBER]: {
            ...testForNoCommittee(committees?.a === 2, audCom, lseCompanyMeta),
        },
        [COLUMN.COMMITTEE.REMUNERATION.CHAIRS]: {
            borderLeft,
            ...testForNoCommittee(committees?.r === 1, remCom, lseCompanyMeta),
        },
        [COLUMN.COMMITTEE.REMUNERATION.MEMBER]: {
            ...testForNoCommittee(committees?.r === 2, remCom, lseCompanyMeta),
        },
        [COLUMN.COMMITTEE.NOMINATION.CHAIRS]: {
            borderLeft,
            ...testForNoCommittee(committees?.n === 1, nomCom, lseCompanyMeta),
        },
        [COLUMN.COMMITTEE.NOMINATION.MEMBER]: {
            ...testForNoCommittee(committees?.n === 2, nomCom, lseCompanyMeta),
        },
    }

    const tenure = commonLseTableData({ apptInfo, column: COLUMN.TENURE })
    setStats(apptInfo.tenureYears, stats[COLUMN.TENURE], true)

    const officer = getCellWithTooltip({
        sortValue: commonInfo.name,
        tooltip,
        type: string,
        value: firstToLast(commonInfo.name),
    })

    const company = getCellWithTooltip({
        tooltip,
        paddingLeft: showLogos ? 0 : LAYOUT.HOME.PADDING.BORDER,
        type: string,
        value: lseCompanyMeta.names[0],
    })

    setStats(1, stats[COLUMN.COMPANY], true)

    const ac = setStatsAndGetCell(COLUMN.COMMITTEE.AUDIT.CHAIRS, cellSpec, stats)
    const am = setStatsAndGetCell(COLUMN.COMMITTEE.AUDIT.MEMBER, cellSpec, stats)
    const rc = setStatsAndGetCell(COLUMN.COMMITTEE.REMUNERATION.CHAIRS, cellSpec, stats)
    const rm = setStatsAndGetCell(COLUMN.COMMITTEE.REMUNERATION.MEMBER, cellSpec, stats)
    const nc = setStatsAndGetCell(COLUMN.COMMITTEE.NOMINATION.CHAIRS, cellSpec, stats)
    const nm = setStatsAndGetCell(COLUMN.COMMITTEE.NOMINATION.MEMBER, cellSpec, stats)

    const position = getCellWithTooltip({
        borderLeft,
        paddingLeft: LAYOUT.HOME.PADDING.BORDER,
        tooltip,
        type: string,
        value: apptInfo.position.title ? apptInfo.position.title : apptInfo.role,
    })

    const age = getCellWithTooltip({
        borderLeft,
        tooltip,
        type: number,
        value: personInfo.age
    })

    setStats(personInfo.age, stats[COLUMN.AGE])

    const sex = getCellWithTooltip(
        personInfo.sex === 1
            ? {
                align: right,
                tooltip: "female",
                type: string,
                value: "F",
            }
            : {
                align: right,
                tooltip: personInfo.sex === 0 ? "male" : "unknown",
                type: string,
                value: personInfo.sex === 0 ? "M" : JARGON.UNKNOWN,
            }
    )

    if (personInfo.sex === 0 || personInfo.sex === 1) {
        stats[COLUMN.SEX].total += personInfo.sex
        stats[COLUMN.SEX].numof++
    }

    const logo = showLogos
        ? lseCompanyMeta?.logo ?? "no logo"
        : null

    const cellWithTooltips = portrait
        ? [company, officer, ac, am]
        : [company, officer, position]

    if (!portrait) {
        if (columns[COLUMN.TENURE]) cellWithTooltips.push(tenure)
        cellWithTooltips.push(ac, am, rc, rm, nc, nm)
        if (columns[COLUMN.AGE]) cellWithTooltips.push(age)
        if (columns[COLUMN.SEX]) cellWithTooltips.push(sex)
        addPaddingRightToLastCell(cellWithTooltips)
    }

    return {
        index,
        fontWeight,
        link: `/company/${lseCompanyMeta.ids[0]}`,
        logo,
        cellWithTooltips,
    }
}

const getPositionFontWeight = positionType =>
    (positionType >= 0 && POSITION[positionType].FONT_WEIGHT)
        ? POSITION[positionType].FONT_WEIGHT : normal
//#endregion Rows

